import { addLinkAssetAttachments } from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import { useWorkOrderModalsContext } from "@/shared/work-orders-modals/context/work-order-modals.context";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Button,
	List,
	ListItem,
	ListItemText,
} from "@material-ui/core";
import { useEffect, useState } from "react";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import type { WorkOrderAttachment } from "../../../models/WorkOrderAttachment";

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	AssetId: number | string;
	searchAssetResources?: Function;
	newUploadedAttachments: WorkOrderAttachment[];
	WorkOrderName: string;
	AssetName: string;
	suffixTitle?: string;
};

export const AssetAddAttachmentDialog = ({
	show,
	onHide,
	AssetId,
	searchAssetResources,
	newUploadedAttachments,
	WorkOrderName,
	AssetName,
	suffixTitle,
}: Props) => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const [showDialog, setShowDialog] = useState(false);
	const [attachmentsToAdd, setAttachmentsToAdd] = useState<
		WorkOrderAttachment[]
	>([]);

	const {
		state: { assetAttachments },
	} = useWorkOrderModalsContext();

	useEffect(() => {
		if (show && newUploadedAttachments.length > 0) {
			const newAttachments = newUploadedAttachments.filter(
				(newAtt) =>
					!assetAttachments.some(
						(assetAtt: any) => assetAtt.UploadFileId === newAtt.UploadFileId,
					),
			);

			if (newAttachments.length > 0) {
				setAttachmentsToAdd(newAttachments);
				setShowDialog(true);
			} else {
				onHide(false);
			}
		} else {
			setShowDialog(false);
		}
	}, [show, newUploadedAttachments, assetAttachments]);

	const addResourcesToAsset = async () => {
		setIsLoading(true);
		try {
			for (const attachment of attachmentsToAdd) {
				await addLinkAssetAttachments(
					AssetId,
					attachment.UploadFileId,
					attachment.FileName,
				);
			}

			if (searchAssetResources) await searchAssetResources();
			setShowDialog(false);
			onHide(true);
		} catch (err) {
			setError("Failed to add attachments to asset");
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		setShowDialog(false);
		onHide(false);
	};

	if (!showDialog) {
		return null;
	}

	return (
		<>
			{showDialog && (
				<HelmetDexteel
					title={`Add Attachments${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<Dialog
				open={showDialog}
				onClose={handleClose}
				aria-labelledby="responsive-dialog-title"
			>
				<DialogTitle style={{ paddingBottom: 6 }} id="responsive-dialog-title">
					{attachmentsToAdd?.length > 1
						? `Add attachments to Asset: ${AssetName}`
						: `Add attachment to Asset: ${AssetName}`}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						The following attachments have been added to the work order{" "}
						{WorkOrderName}. Would you like to add them to the corresponding
						asset as well?
					</DialogContentText>
					<List>
						{attachmentsToAdd.map((attachment) => (
							<ListItem key={attachment.UploadFileId}>
								<ListItemText primary={attachment.FileName} />
							</ListItem>
						))}
					</List>
				</DialogContent>
				<DialogActions style={{ padding: "24px" }}>
					<Button
						variant="contained"
						fullWidth
						color="default"
						onClick={handleClose}
						disabled={isLoading}
					>
						SKIP
					</Button>
					<Button
						fullWidth
						variant="contained"
						onClick={addResourcesToAsset}
						color="primary"
						autoFocus
						disabled={isLoading}
					>
						{isLoading ? "ADDING..." : "ADD"}
					</Button>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
